import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Heart, MessageSquare, MapPin, HandHelping, CheckCircle, Users, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const VARIANTS = {
  watchlist: {
    icon: Heart,
    title: 'Save This for Later',
    description: 'Create a free account to save listings, build your watchlist, and get back to them anytime.',
    cta: 'Sign Up to Save',
  },
  message: {
    icon: MessageSquare,
    title: 'Message This Business',
    description: 'Create a free account to message businesses directly, get quotes, and ask questions.',
    cta: 'Sign Up to Message',
  },
  follow: {
    icon: MapPin,
    title: 'Follow Your Town',
    description: 'Create a free account to follow towns and get updates on new businesses, jobs, and events near you.',
    cta: 'Sign Up to Follow',
  },
  request: {
    icon: HandHelping,
    title: 'Get Help from Local Pros',
    description: 'Create a free account to post a request and receive quotes from trusted local professionals.',
    cta: 'Sign Up to Post',
  },
  claim: {
    icon: Building2,
    title: 'Claim Your Business',
    description: 'Create a free account to take ownership of this listing, update your details, and start attracting local customers.',
    cta: 'Sign Up to Claim',
  },
  community: {
    icon: Users,
    title: 'Join the Conversation',
    description: 'Create a free account to post updates, ask questions, vote in polls, and connect with your local community.',
    cta: 'Sign Up to Post',
  },
  nominate: {
    icon: Heart,
    title: 'Nominate Your Favourite',
    description: 'Create a free account to suggest the businesses you love and help build the ultimate Essex guide.',
    cta: 'Sign Up to Nominate',
  },
  vote: {
    icon: Users,
    title: 'Vote for Your Favourite',
    description: 'Create a free account to vote for the businesses you think deserve a spot on this list.',
    cta: 'Sign Up to Vote',
  },
};

const PERKS = [
  'No credit card required',
  'Takes less than 30 seconds',
  'Free forever — no hidden costs',
];

const SignUpPromptModal = ({ isOpen, onClose, variant = 'watchlist', redirectPath }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const v = VARIANTS[variant] || VARIANTS.watchlist;
  const Icon = v.icon;

  const handleSignUp = () => {
    onClose();
    const redirect = redirectPath || window.location.pathname + window.location.search;
    navigate(`/signup?redirect=${encodeURIComponent(redirect)}`);
  };

  const handleLogin = () => {
    onClose();
    const redirect = redirectPath || window.location.pathname + window.location.search;
    navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="signup-prompt-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 transition-colors z-10"
          data-testid="signup-prompt-close"
        >
          <X className="h-4 w-4 text-slate-400" />
        </button>

        {/* Header */}
        <div className="bg-[#0F2C3E] px-6 pt-8 pb-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center mx-auto mb-4">
            <Icon className="h-7 w-7 text-[#D4A017]" />
          </div>
          <h3 className="text-xl font-bold text-white mb-1.5">{v.title}</h3>
          <p className="text-sm text-white/65 leading-relaxed">{v.description}</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <Button
            onClick={handleSignUp}
            className="w-full bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] font-bold py-5 rounded-xl text-base mb-3"
            data-testid="signup-prompt-cta"
          >
            {v.cta}
          </Button>

          <button
            onClick={handleLogin}
            className="w-full text-center text-sm text-slate-500 hover:text-[#0F2C3E] transition-colors py-2"
            data-testid="signup-prompt-login"
          >
            Already have an account? <span className="font-medium underline">Log in</span>
          </button>

          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
            {PERKS.map(perk => (
              <div key={perk} className="flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle className="h-3.5 w-3.5 text-[#4A6741] flex-shrink-0" />
                {perk}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPromptModal;
